import type { CalendarDay } from '@klopotun/contracts';
import { formatCivilDate } from '@klopotun/domain';
import { formatMonthName } from '../../../payment-view/index.js';
import { formatCompactAmount, formatMonthTitle } from '../../../summary-view/index.js';
import { monthGridOf, WEEKDAY_LABELS, type MonthGridCell } from '../../lib/month-grid.js';
import styles from './month-grid.module.css';

type MonthGridProps = {
  year: number;
  month: number;
  /** The days of the month that carry something, by their ISO date. */
  daysByDate: Map<string, CalendarDay>;
  today: string;
  selected: string | null;
  baseCurrency: string;
  onSelect: (date: string) => void;
};

const APPROXIMATELY = '≈';

const cellClassName = (isToday: boolean, isSelected: boolean, hasEntries: boolean): string =>
  [
    styles.day,
    isToday ? styles.today : '',
    isSelected ? styles.selected : '',
    hasEntries ? styles.hasEntries : '',
  ]
    .filter((name) => name !== '')
    .join(' ');

const amountOf = (day: CalendarDay | undefined, baseCurrency: string): string | null => {
  if (day === undefined) {
    return null;
  }

  const compact = formatCompactAmount(day.totalMinor, baseCurrency);

  return day.isComplete ? compact : `${APPROXIMATELY}${compact}`;
};

const labelOf = (cell: NonNullable<MonthGridCell>, amount: string | null): string => {
  const dayText = `${String(cell.day)} ${formatMonthName(cell.month)}`;

  return amount === null ? dayText : `${dayText}, ${amount}`;
};

const keyOf = (cell: MonthGridCell, weekIndex: number, dayIndex: number): string =>
  cell === null ? `blank-${String(weekIndex)}-${String(dayIndex)}` : formatCivilDate(cell);

type DayCellProps = {
  cell: MonthGridCell;
  daysByDate: Map<string, CalendarDay>;
  today: string;
  selected: string | null;
  baseCurrency: string;
  onSelect: (date: string) => void;
};

const DayCell = ({ cell, daysByDate, today, selected, baseCurrency, onSelect }: DayCellProps) => {
  if (cell === null) {
    return <span className={styles.blank} aria-hidden="true" />;
  }

  const date = formatCivilDate(cell);
  const day = daysByDate.get(date);
  const amount = amountOf(day, baseCurrency);
  const isSelected = selected === date;

  return (
    <button
      type="button"
      className={cellClassName(today === date, isSelected, day !== undefined)}
      aria-pressed={isSelected}
      aria-label={labelOf(cell, amount)}
      onClick={() => {
        onSelect(date);
      }}
    >
      <span className={styles.number}>{cell.day}</span>
      {amount !== null && <span className={styles.amount}>{amount}</span>}
    </button>
  );
};

export const MonthGrid = ({
  year,
  month,
  daysByDate,
  today,
  selected,
  baseCurrency,
  onSelect,
}: MonthGridProps) => (
  <div
    className={styles.grid}
    role="grid"
    aria-label={`${formatMonthTitle(formatMonthName(month))} ${String(year)}`}
  >
    {WEEKDAY_LABELS.map((label) => (
      <span key={label} className={styles.weekday} aria-hidden="true">
        {label}
      </span>
    ))}
    {monthGridOf(year, month).flatMap((week, weekIndex) =>
      week.map((cell, dayIndex) => (
        <DayCell
          key={keyOf(cell, weekIndex, dayIndex)}
          cell={cell}
          daysByDate={daysByDate}
          today={today}
          selected={selected}
          baseCurrency={baseCurrency}
          onSelect={onSelect}
        />
      )),
    )}
  </div>
);
